/**
 * One of each, always.
 *
 * Every product here is a digital download, so a line quantity above one is
 * always a mistake: it charges twice for the same file and creates a refund.
 * The cart stepper is hidden, but that is presentation only - Shopify still
 * increments a line when the same product is added twice.
 *
 * WHY THIS IS WRITTEN THE WAY IT IS
 *
 * A previous version checked the cart with `await` inside the submit handler
 * and then called preventDefault(). That can never work: once a handler awaits,
 * the event has finished dispatching and the default action has already run.
 * The add went through every time.
 *
 * So the decision has to be SYNCHRONOUS. A small set of the variant ids already
 * in the cart is kept in memory, and the submit handler consults that set with
 * no awaiting at all.
 *
 * The set is kept fresh by wrapping fetch and watching for calls to /cart/*,
 * rather than listening for theme events. Horizon's event names come from an
 * external Shopify module, so matching on them by hand is guesswork; every cart
 * mutation goes through fetch regardless of which component made it.
 */
(() => {
  /** Variant ids currently in the cart, as strings. */
  const inCart = new Set();
  let refreshing = false;

  const readCart = async () => {
    if (refreshing) return null;
    refreshing = true;
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null; // 429 answers with HTML; do not try to parse it
      const cart = await res.json();
      inCart.clear();
      (cart.items || []).forEach((i) => inCart.add(String(i.variant_id)));
      return cart;
    } catch (e) {
      return null;
    } finally {
      refreshing = false;
    }
  };

  /** Pull any line above 1 back down to 1. */
  const clamp = async () => {
    const cart = await readCart();
    if (!cart || !cart.items) return;

    const updates = {};
    cart.items.forEach((item, i) => {
      if (item.quantity > 1) updates[i + 1] = 1; // `updates` is 1-indexed
    });
    if (!Object.keys(updates).length) return;

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (res.ok) window.location.reload();
    } catch (e) {
      /* a failed clamp must never block checkout */
    }
  };

  /* --- Layer 1: refuse the duplicate add, synchronously --- */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || !form.matches) return;
      if (!form.matches('form[action*="/cart/add"], form[data-type="add-to-cart-form"]')) return;

      const field = form.querySelector('[name="id"]');
      if (!field || !inCart.has(String(field.value))) return;

      // Already in the cart. Stop the add before anything else sees the event.
      event.preventDefault();
      event.stopImmediatePropagation();

      const opener = document.querySelector('[data-cart-drawer-open], .header__cart, a[href$="/cart"]');
      if (opener && opener.click) opener.click();
    },
    true // capture, so this runs ahead of the theme's own handler
  );

  /* --- Keep the set current by watching cart traffic --- */
  const nativeFetch = window.fetch;
  window.fetch = function (...args) {
    const result = nativeFetch.apply(this, args);
    try {
      const url = String(args[0] && args[0].url ? args[0].url : args[0] || '');
      if (url.indexOf('/cart/') > -1 || url.indexOf('/cart.js') > -1) {
        result.then(() => setTimeout(clamp, 250)).catch(() => {});
      }
    } catch (e) {
      /* never let bookkeeping break a real request */
    }
    return result;
  };

  const start = () => clamp();
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();
